import { Search as Magnifier } from 'lucide-react';
import axios from 'axios';
import googlePinIcon from 'images/google-buttons/google_pin.svg';
import * as React from 'react';
import styled from 'styled-components';
import Input from '../../../components_v2/input/Input';
import PageLoader from '../../../components_v2/pageLoader/PageLoader';
import { URL_FOUNDATION } from '../../../config/keys';
import CloseIcon from '../../../styles/custom-icons/close';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { PrimaryColor, GreySidely, SidelyBlack, SurfaceColor } from '../../../styles/global/css/Utils';
import { Translate } from '../../../styles/global/translate';
import { useFunctionState } from '../../../utils/customHooks';
import { NoMarginH6, NoMarginP } from '../../authorization/users/Members';
import { LoadingStateEnum } from '../../import/model';
import { GooglePlaceItem } from '../model';

const SEARCH_BAR_HEIGHT = 45;
const SEARCH_BAR_PADDING = 8;
const SEARCH_BAR_OPEN_WIDTH = 250;
const SEARCH_BAR_TOP = 10;

const ContainerDiv = styled.div`
  height: ${SEARCH_BAR_HEIGHT + SEARCH_BAR_TOP}px;
  z-index: 1;
  position: relative;
`;

const GooglePointsContainer = styled.div<{
	open: boolean;
	top: number;
}>`
  position: absolute;
  height: calc(100vh - ${p => p.top}px);
  width: calc(${SEARCH_BAR_OPEN_WIDTH}px + ${SEARCH_BAR_PADDING}px);
  padding: ${SEARCH_BAR_PADDING}px;
  background-color: ${SurfaceColor};
  top: 0px;
  right: ${props =>
		props.open
			? '0'
			: `calc(-${SEARCH_BAR_OPEN_WIDTH}px - ${SEARCH_BAR_PADDING}px)`};
  transition: right 0.3s ease-in-out;
  padding-top: ${SEARCH_BAR_HEIGHT + SEARCH_BAR_TOP * 2}px;
  overflow: auto;
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 5px;
`;

const GooglePointsFooter = styled.div`
  width: 100%;
  margin-bottom: 20px;
  justify-content: center;
  display: flex;
`;

const SearchBar = styled.div<{ open: boolean }>`
  background-color: ${SurfaceColor};
  border-radius: ${SEARCH_BAR_HEIGHT}px 0 0 ${SEARCH_BAR_HEIGHT}px;
  width: ${SEARCH_BAR_OPEN_WIDTH}px;
  padding: ${SEARCH_BAR_PADDING}px;
  position: relative;
  right: ${({ open }) =>
		open
			? '0'
			: `calc(${SEARCH_BAR_HEIGHT}px - ${SEARCH_BAR_OPEN_WIDTH}px)`};
  transition: right 0.3s ease-in-out;
  display: flex;
  align-items: center;
  height: ${SEARCH_BAR_HEIGHT}px;
  gap: ${SEARCH_BAR_PADDING}px;
  top: ${SEARCH_BAR_TOP}px;
  box-shadow: -2px 0px 4px 0px color-mix(in oklab, ${SidelyBlack} 24%, transparent);
`;

const GooglePinImage = styled.img`
  width: ${SEARCH_BAR_HEIGHT - SEARCH_BAR_PADDING * 2}px;
  height: ${SEARCH_BAR_HEIGHT - SEARCH_BAR_PADDING * 2}px;
  flex-shrink: 0;
  cursor: pointer;
`;

const CloseContainer = styled.button<{ open: boolean }>`
	position: absolute;
	left: ${({ open }) => (open ? '0' : '100%')};
	background: ${SurfaceColor};
	border-radius: 50%;
	aspect-ratio: 1 / 1;
	padding: 5px;
	z-index: -1;
	transition: left 0.3s ease-in-out;
	translate: calc(-100% - 15px);
	box-shadow: -2px 0px 4px 0px color-mix(in oklab, ${SidelyBlack} 24%, transparent) !important;
	border: none;
`;

const SearchInArea = styled(CloseContainer)`
	translate: ${({ open }) => (open ? 'calc(-100% - 15px) calc(100% + 15px)' : '0 calc(100% + 15px)')};
	border-radius: 100px;
	height: unset;
	overflow: hidden;
	aspect-ratio: unset;
	&: hover {
		max-width: 200px;
	}
	padding: 10px;
  	display: flex;
  	gap: 5px;
  	align-items: center;
   	height: 40px;
    max-width: 40px;
    transition: 0.5s;
`;

type Query = {
	query: string;
	bounds: {
		viewport_sw: { lat: number; lng: number };
		viewport_ne: { lat: number; lng: number };
	};
};


export default function MapSearch(props: {
	map: {
		getBounds: () => {
			getSouthWest: () => { lat: number; lng: number };
			getNorthEast: () => { lat: number; lng: number };
		};
		getDiv: () => { firstChild: HTMLDivElement };
		setCenter: (center: { lat: number; lng: number }) => void;
		setZoom: (zoom: number) => void;
		addListener: (event: string, callback: () => void) => void;
		removeListener: (event: string, callback: () => void) => void;
	};
	setGooglePlaces: (places: GooglePlaceItem[]) => void;
	convertCompany?: (data: GooglePlaceItem[]) => void;
}) {
	const [open, setOpen] = React.useState(false);
	const [nextPageToken, setNextPageToken] = React.useState<string>();
	const [loadingState, setLoadingState] = React.useState<LoadingStateEnum>(
		LoadingStateEnum.LOADED
	);
	const [lastQuery, setLastQuery] = React.useState<Query>();
	const [query, setQuery] = useFunctionState('', undefined, () =>
		setNextPageToken(undefined)
	);
	const [boundsChanged, setBoundsChanged] = React.useState(false);
	const [googlePlaces, setGooglePlaces] = useFunctionState<GooglePlaceItem[]>(
		[],
		undefined,
		props.setGooglePlaces
	);

	React.useEffect(() => {
		const listener = () => {
			setBoundsChanged(true);
		};
		props.map.addListener('bounds_changed', listener);
		return () => {
			props.map.removeListener('bounds_changed', listener);
		};
	}, []);

	const search = async(nextPageToken?: string, force?: boolean) => {
		if (!force && (!query || (query === lastQuery?.query && !nextPageToken))) return;
		try {
			const t = props.map.getBounds();
			const bounds = { viewport_sw: t.getSouthWest(), viewport_ne: t.getNorthEast() };
			const newQuery = { query, bounds: nextPageToken ? lastQuery?.bounds ?? bounds : bounds };
			setLastQuery(newQuery);
			setLoadingState(LoadingStateEnum.LOADING);
			const res = (await axios.post<{
				places: GooglePlaceItem[];
				next_page_token?: string;
			}>(`${URL_FOUNDATION}/api/map/search-places`, {
				query,
				page_token: nextPageToken,
				...newQuery.bounds,
			})).data;
			setLoadingState(LoadingStateEnum.LOADED);
			if (res) {
				setGooglePlaces(prevPlaces =>
					nextPageToken ? [...prevPlaces, ...res.places] : res.places
				); // Append new places
				setNextPageToken(res.next_page_token); // Update next page token
			}
		} catch (e) {
			console.error(e);
			setLoadingState(LoadingStateEnum.LOADED); // Reset loading state on error
		}
	};

	return (
		<ContainerDiv>
			<GooglePointsContainer
				open={googlePlaces.length > 0 && open}
				top={props.map.getDiv().firstChild!.getBoundingClientRect().top}
				onScroll={e => {
					const target = e.target as HTMLElement;
					if (target.scrollHeight - target.scrollTop === target.clientHeight) {
						// User has scrolled to the bottom
						if (nextPageToken && loadingState === LoadingStateEnum.LOADED) {
							search(nextPageToken); // Call the search function to fetch more data
						}
					}
				}}
			>
				{googlePlaces.map(p => (
					<PlaceElement
						key={p.id}
						place={p}
						convertCompany={props.convertCompany}
						centerPoint={(lat, lng) => {
							props.map.setCenter({ lat, lng });
							props.map.setZoom(15);
						}}
					/>
				))}
				<GooglePointsFooter>
					{loadingState === LoadingStateEnum.LOADING && (
						<PageLoader size={20} color={GreySidely} weight={1} />
					)}
					{!nextPageToken && loadingState === LoadingStateEnum.LOADED && (
						<NoMarginP>
							<Translate id='end_of_results' />
						</NoMarginP>
					)}
				</GooglePointsFooter>
			</GooglePointsContainer>
			<SearchBar open={open}>
				<CloseContainer open={open} onClick={() => setOpen(!open)}><CloseIcon size={30} /></CloseContainer>
				<SearchInArea open={open && boundsChanged && googlePlaces.length > 0} onClick={() => {
					setBoundsChanged(false);
					search(undefined, true);
				}}>
					<Magnifier size={20} style={{ flexShrink: 0 }} />
					<NoMarginH6 style={{ flexShrink: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
						<Translate id='search_in_area' />
					</NoMarginH6>
				</SearchInArea>
				<GooglePinImage
					src={googlePinIcon}
					alt="Google Pin"
					onClick={() => setOpen(!open)}
				/>
				{open && <Input
					autoFocus
					name="google-search"
					type="text"
					placeholder="Search in google map"
					inputStyle={{ width: '100%', border: 'none', padding: '0px', containerWidth: '100%' }}
					onChange={setQuery}
					value={query}
					onBlur={() => {
						setBoundsChanged(false);
						search();
					}}
				/>}
				<PageLoader size={20} color={loadingState === LoadingStateEnum.LOADING ? GreySidely : 'transparent'} weight={1} />
			</SearchBar>
		</ContainerDiv>
	);
}

const PlaceElementContainer = styled.div`
  display: flex;
  flex-flow: column;
  gap: 10px;
  padding: 10px;
  width: 100%;
  border-radius: 5px;
  cursor: pointer;

  &:hover {
  	background-color: color-mix(in oklab, ${PrimaryColor} 10%, transparent);
  }
`;

const H4 = styled.h4`
  margin: 0;
`;

function PlaceElement({
	place,
	convertCompany,
	centerPoint
}: {
	place: GooglePlaceItem;
	convertCompany?: (data: GooglePlaceItem[]) => void;
	centerPoint?: (lat: number, lng: number) => void;
}) {
	return (
		<PlaceElementContainer onClick={() => centerPoint?.(place.location.latitude, place.location.longitude)}>
			<H4>{place.displayName.text}</H4>
			<NoMarginP>{place.formattedAddress}</NoMarginP>
			{convertCompany && (
				<DefaultButton
					margin="10px 0"
					width="fit-content"
					onClick={() => convertCompany([place])}
				>
					<Translate id="convert_in_company" />
				</DefaultButton>
			)}
		</PlaceElementContainer>
	);
}
